import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";

import { GRAPH_TYPE } from "../../constants/trendGraph/trendGraphType";
import { COLORS } from "../../constants/colors";
import { formatDate } from "../../util/timezone";
import { FULL_DATE_TIME_FORMAT } from "../../constants/util";
import { visibilityIcon } from "../../../assets/images/visibility/icon-watch.js";
import { hideIcon } from "../../../assets/images/visibility/icon-hide.js";
import { ELECTRIC_TREND_TYPE } from "../../constants/trendGraph/electricLines.js";

export const initYAxis = (
  root,
  chart,
  ganttOnly = false,
  lineOnly = false,
  isGantt = false,
) => {
  const valueAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      height: ganttOnly ? am5.percent(0) : lineOnly ? am5.percent(100) : 350,
      maxPrecision: 3,
      renderer: am5xy.AxisRendererY.new(root, {}),
      autoZoom: false,
      tooltip: isGantt ? null : am5.Tooltip.new(root, {}),
    })
  );
  const yRenderer = valueAxis.get("renderer");
  yRenderer.labels.template.setAll({
    fill: am5.color(COLORS.white),
    opacity: 0.2,
    fontSize: "14px",
  });
  yRenderer.grid.template.setAll({
    stroke: am5.color(COLORS.white),
    strokeWidth: 2,
    strokeDasharray: [4, 2],
  });
  return valueAxis;
};

export const initGanttYAxis = (root, chart) => {
  const valueAxisGantt = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      height: 15,
      renderer: am5xy.AxisRendererY.new(root, {}),
      min: 0,
      max: 1,
      maxPrecision: 0,
      autoZoom: false,
      zoomable: false,
      marginBottom: 20,
    })
  );
  const yRenderer = valueAxisGantt.get("renderer");
  yRenderer.labels.template.setAll({
    fill: am5.color(COLORS.white),
    opacity: 0.2,
    fontSize: "14px",
  });
  yRenderer.grid.template.setAll({
    stroke: am5.color(COLORS.white),
    strokeWidth: 2,
    strokeDasharray: [4, 2],
  });
  return valueAxisGantt;
};

export const initTooltip = (root, chart, isLocked) => {
  let lockedPositionX;
  let lockedPositionY;
  const existingTooltip = chart.plotContainer.getTooltip();
  if (existingTooltip) {
    lockedPositionX = chart.plotContainer._settings.tooltipX;
    lockedPositionY = chart.plotContainer._settings.tooltipY;
    existingTooltip.dispose();
  }
  const tooltip = am5.Tooltip.new(root, {"pointerOrientation": "horizontal"});
  if (isLocked) {
    chart.plotContainer.setAll({
      tooltipPosition: "fixed",
      tooltipX: lockedPositionX,
      tooltipY: lockedPositionY,
      tooltip,
      tooltipText: "a",
      showTooltipOn: "always",
    });
    tooltip.get("background").setAll({
      fill: am5.color(0x27303d),
      fillOpacity: 0.8,
      stroke: am5.color(0xfea813),
    });
  } else {
    chart.plotContainer.setAll({
      tooltipPosition: "pointer",
      tooltipText: "a",
      tooltip,
    });

    tooltip.get("background").setAll({
      fill: am5.color(0x27303d),
      fillOpacity: 0.8,
      stroke: am5.color(0xffffff),
      strokeOpacity: 1,
    });
  }
  return tooltip;
};

export const initTooltipLabel = (chart, tooltip, type, t, timezone, localizationKeys) => {
  let width = 230;
  switch (type) {
    case GRAPH_TYPE.ELECTRIC:
      width = 130;
      break;
    case GRAPH_TYPE.ENGINE:
    case GRAPH_TYPE.SEP:
    case GRAPH_TYPE.CUSTOM:
      break;
  }

  tooltip.label.adapters.add("text", (text) => {
    text = t(localizationKeys.NoData_lower);
    let hasTooltipData = false;
    const displayDigitalList = [];
    // const hiddenDigitalList = [];
    chart.series.each(series => {
      if (series instanceof am5xy.ColumnSeries) {
        if(series.get('visible')) displayDigitalList.push(series.get("key"));
        // if (!series.get("visible")) hiddenDigitalList.push(series.get("key"))
      }
    });
    chart.series.each(function (series, i) {
      const tooltipDataItem = series.get("tooltipDataItem");
      const currName = series.get("name");
      if (tooltipDataItem && !currName.includes("test") && series instanceof am5xy.LineSeries) {
        hasTooltipData = true;
        if (i === 0) {
          const dateStr = tooltipDataItem.get("valueX");
          // const date = timezone === TIMEZONE.UTC ? `${dayjs.utc(dateStr).format(FULL_DATE_TIME_FORMAT)} (${timezone}) \n` : `${dayjs(dateStr).format(FULL_DATE_TIME_FORMAT)} (${timezone})\n`;
          const date = formatDate(dateStr, timezone, FULL_DATE_TIME_FORMAT);
          text += `[bold]${date}[/]`;
          text = text.replace(t(localizationKeys.NoData_lower), "");
        }
        if (!series.isHidden() && displayDigitalList.includes(series.get("key"))) {
          const digitalStatus = tooltipDataItem?.dataContext?.status;
          const valueY = tooltipDataItem.get("valueY") === undefined ? digitalStatus : tooltipDataItem.get("valueY");
          const strokeColor = series.get("stroke").toString();
          text += `\n[${strokeColor} width: ${width}px]${series.get(
            "name"
          )}:[/] [${strokeColor}]${valueY !== null && valueY !== undefined ? valueY : "--"}[/]`;
        }
      }
      text = text.replace(t(localizationKeys.NoData_lower), "");
    });

    if (!hasTooltipData) {
      tooltip.set("forceHidden", true);
    } else {
      tooltip.set("forceHidden", false);
    }

    return text;
  });
};

export const initCursor = (chart, root, inxAxis, isMockPreview, isMobile, isGanttChart = false) => {
  const cursorinstance = chart.set(
    "cursor",
    am5xy.XYCursor.new(root, {
      xAxis: inxAxis,
      yAxis: null,
      behavior: isMockPreview || isMobile ? "none" : isGanttChart ? "zoomX" : "zoomXY",
      fullWidthLineX: true,
      fullWidthLineY: !isGanttChart,
    })
  );
  cursorinstance.lineX.setAll({
    stroke: am5.color(0xffffff),
    strokeWidth: 2,
    strokeDasharray: [],
  });
  cursorinstance.lineY.setAll({
    stroke: am5.color(0xffffff),
    strokeWidth: 2,
    strokeDasharray: [],
  });
  cursorinstance.selection.setAll({
    fill: am5.color(0xffffff),
    fillOpacity: 0.2,
  });

  return cursorinstance;
};

export const initScrollbarX = (chart, root, isMockPreview, isLocked, handleUnlock) => {
  const minGripWidth = 40;
  const scrollbarX = am5.Scrollbar.new(root, {
    orientation: "horizontal",
  });
  const isExpand = root.dom.id.includes("expand");
  scrollbarX.thumb.setAll({
    fill: isExpand ? am5.color("#626970") : am5.color(0x2c3a48),
    stroke: am5.color(0x020202),
    strokeOpacity: 0.3,
    minWidth: minGripWidth,
  });
  scrollbarX.get("background").setAll({
    fill: am5.color(0x020202),
    fillOpacity: 0.2,
  });
  scrollbarX.startGrip.get("background").setAll({
    fill: am5.color(0x2c3a48),
    maxWidth: 20,
    maxHeight: 20,
    centerX: -5,
    centerY: -5,
  });
  scrollbarX.endGrip.get("background").setAll({
    fill: am5.color(0x2c3a48),
    maxWidth: 20,
    maxHeight: 20,
    centerX: -5,
    centerY: -5,
  });
  scrollbarX.events.on("rangechanged", () => {
    if (isLocked) {
      handleUnlock();
    }
  });
  const startGripHandler = () => {
    if(scrollbarX.endGrip.x() - scrollbarX.startGrip.x() <= minGripWidth){
      scrollbarX.startGrip.set('x', scrollbarX.endGrip.x() - minGripWidth);
    }
  };
  const endGripHandler = () => {
    if(scrollbarX.endGrip.x() - scrollbarX.startGrip.x() <= minGripWidth){
      scrollbarX.endGrip.set('x', scrollbarX.startGrip.x() + minGripWidth);
    }
  };
  scrollbarX.startGrip.events.on("dragged", startGripHandler);
  scrollbarX.startGrip.events.on("positionchanged", startGripHandler);
  scrollbarX.endGrip.events.on("dragged", endGripHandler);
  scrollbarX.endGrip.events.on("positionchanged", endGripHandler);

  if (isMockPreview) {
    scrollbarX.hide(0);
  }
  chart.set("scrollbarX", scrollbarX);
  chart.bottomAxesContainer.children.push(scrollbarX);

  return scrollbarX;
};

export const initScrollbarY = (chart, root, isMockPreview, isLocked, handleUnlock) => {
  const minGripHeight = 50;
  const isExpand = root.dom.id.includes("expand");
  const scrollbarY = am5.Scrollbar.new(root, {
    orientation: "vertical",
    height: am5.percent(100)
  });
  scrollbarY.thumb.setAll({
    fill: isExpand ? am5.color("#626970") : am5.color(0x2c3a48),
    stroke: am5.color(0x020202),
    strokeOpacity: 0.3,
    minHeight: minGripHeight,
  });
  scrollbarY.get("background").setAll({
    fill: am5.color(0x020202),
    fillOpacity: 0.2,
  });
  scrollbarY.startGrip.get("background").setAll({
    fill: am5.color(0x2c3a48),
    maxWidth: 20,
    maxHeight: 20,
    centerX: -5,
    centerY: -5,
  });
  scrollbarY.endGrip.get("background").setAll({
    fill: am5.color(0x2c3a48),
    maxWidth: 20,
    maxHeight: 20,
    centerX: -5,
    centerY: -5,
  });
  scrollbarY.events.on("rangechanged", () => {
    if (isLocked) {
      handleUnlock();
    }
  });
  const startGripHandler = () => {
    if(scrollbarY.endGrip.y() - scrollbarY.startGrip.y() <= minGripHeight){
      scrollbarY.startGrip.set('y', scrollbarY.endGrip.y() - minGripHeight);
    }
  };
  const endGripHandler = () => {
    if(scrollbarY.endGrip.y() - scrollbarY.startGrip.y() <= minGripHeight){
      scrollbarY.endGrip.set('y', scrollbarY.startGrip.y() + minGripHeight);
    }
  };
  scrollbarY.startGrip.events.on("dragged", startGripHandler);
  scrollbarY.startGrip.events.on("positionchanged", startGripHandler);
  scrollbarY.endGrip.events.on("dragged", endGripHandler);
  scrollbarY.endGrip.events.on("positionchanged", endGripHandler);

  if (isMockPreview) {
    scrollbarY.hide(0);
  }
  chart.set("scrollbarY", scrollbarY);
  chart.rightAxesContainer.children.push(scrollbarY);

  return scrollbarY;
};

export const initLegend = (
  charts,
  chart,
  root,
  setDataKeyName,
  setDisplayDataKeyName,
  setIsDigital,
  setColorPickerPos,
  setDisplayColorPicker,
  setDisplayColorPickerFlag,
  type,
  isGantt = false,
  chartType,
) => {
  let dynamicWidth = {
    label: 130,
    container: 230,
  };

  switch (type) {
    case GRAPH_TYPE.SEP:
    case GRAPH_TYPE.ENGINE:
      dynamicWidth = {
        label: 180,
        container: 300,
      };
      break;
    case GRAPH_TYPE.ELECTRIC:
      if(chartType === ELECTRIC_TREND_TYPE.ELECTRIC_POWER_TREND){
        dynamicWidth = {
          label: 90,
          container: 180,
        };
        break;
      }else{
        dynamicWidth = {
          label: 180,
          container: 300,
        };
        break;
      }
    case GRAPH_TYPE.CUSTOM:
      dynamicWidth = {
        label: 260,
        container: 400,
      };
      break;
    case GRAPH_TYPE.PRESSURE:
      dynamicWidth = {
        label: 90,
        container: 180,
      };
      break;
  }

  let count = 0;
  if (isGantt) {
    if (charts.length === 2) {
      count = charts[0].series.length;
    }
  } else {
    count = chart.series.length;
  }

  const legendScrollbar = am5.Scrollbar.new(root, {
        orientation: "vertical",
      })
  legendScrollbar.get('background').setAll({
    fill: am5.color('rgba(2,2,2,0.3)'),
  });
  legendScrollbar.thumb.setAll({
    fill: am5.color('#2F3A47')
  });

  const legend = chart.rightAxesContainer.children.push(
    am5.Legend.new(root, {
      y: am5.percent(10),
      centerY: am5.percent(10),
      height: am5.percent(130),
      width: dynamicWidth.container,
      layout: root.verticalLayout,
      verticalScrollbar: legendScrollbar,
      marginLeft: 15,
      clickTarget: "none",
      wheelable: false,
    })
  );
  legend.labels.template.setAll({
    fontSize: 16,
    fill: am5.color(0xffffff),
    oversizedBehavior: 'truncate',
    width: dynamicWidth.label,
    maxWidth: dynamicWidth.label,
    height: 18,
  });
  legend.itemContainers.template.setAll({
    marginTop: 8,
    marginRight: 5,
    height: 18,
    width: dynamicWidth.container - 30,
    tooltip: am5.Tooltip.new(root, {
      autoTextColor: false,
      dy: 10,
      pointerOrientation: "up"
    }),
  });
  legend.valueLabels.template.setAll({
    width: am5.p100,
    fontSize: 16,
    textAlign: "right",
    fill: am5.color(COLORS.white),
  })
  //legend click event trick
  // When legend mark click - disable hide behavior and display color picker
  legend.markers.template.interactionsEnabled = true;
  legend.markers.template.events.on("click", (e) => {
    if (chart.get("height")) {
      setColorPickerPos({
        x: Math.ceil(legend.x() + e.point.x - 320),
        y: Math.ceil(legend.y() + e.point.y + 20),
      });
    } else {
      setColorPickerPos({ x: 0, y: 0 });
    }
    const targetName = e.target.dataItem.dataContext.get('key');
    const name = e.target.dataItem.dataContext.get('name')
    const isDigital = e.target.dataItem.dataContext.get('isDigitalChannel')
    chart.series.each(function (item) {
      if (targetName && targetName == item.get('key')) {
        setIsDigital(item.get('isDigitalChannel'));
        setDataKeyName(targetName);
        setDisplayDataKeyName(name);
        setDisplayColorPicker({ flag: targetName, position: 0, isDigital });
        setDisplayColorPickerFlag(true);
        item.hide();
      }
    });
  });
  legend.data.setAll(chart.series.values);
  addLegendFeatures(root, chart, legend);
  return legend;
};

export const addLegendFeatures = (root, chart, legend) => {
  const defaultChildrenLength = 3; // Default number of children in item container
  let flg = false;
  legend.allChildren().forEach((v) => {
    if(!v.dataItem) return;
    // legend tooltip (when legend data is updated, old tooltip data is disposed so need to make every time)
    v.setAll({
      tooltipText: v.dataItem.get('name'),
      tooltip: am5.Tooltip.new(root, {
        autoTextColor: false,
        dy: 10,
        pointerOrientation: "up"
      }),
      fill: am5.color('#000000')
    })
    if(v.dataItem.dataContext.isHidden()){
      v.children.push(notVisibleIcon(root));
    }else{
      v.children.push(blankIcon(root));
    }
    v.events.on("pointerover", (e) => {
      const itemContainer = e.target;
      if(!itemContainer.dataItem) return;
      if (!itemContainer.dataItem.dataContext.isHidden()) {
        if(itemContainer.children.length > defaultChildrenLength){
          itemContainer.children.pop();
        }
        itemContainer.children.push(visibleIcon(root));
      }
      const series = itemContainer.dataItem.dataContext;
      if ("strokes" in series) {
        //for line chart
        chart.series.each((chartSeries) => {
          // if (chartSeries !== series && chartSeries !== highlight.current) {
          if (chartSeries !== series) {
            chartSeries?.strokes?.template.setAll({
              strokeOpacity: 0.15,
              stroke: am5.color(COLORS.white),
            });
          } else {
            chartSeries?.strokes?.template.setAll({
              strokeWidth: 3,
            });
          }
        });
      } else if ("columns" in series) {
        //for bar chart
        chart.series.each((chartSeries) => {
          // if (chartSeries !== series || chartSeries !== highlight) {
          if (chartSeries !== series) {
            chartSeries?.columns?.template.setAll({
              fillOpacity: 0.15,
              fill: am5.color(COLORS.white),
            });
          } else {
            chartSeries?.columns?.template.setAll({
              strokeWidth: 3,
            });
          }
        });
      }
      itemContainer.children.push(hoveredLine(root, itemContainer.width(), itemContainer.height()));
    });
    v.events.on("pointerout", (e) => {
      flg = false;
      const itemContainer = e.target;
      if(!itemContainer.dataItem) return;
      while (e.target.children.length > 3) e.target.children.pop();
      if (itemContainer.dataItem.dataContext.isHidden()) {
        e.target.children.push(notVisibleIcon(root))
      }else{
        e.target.children.push(blankIcon(root));
      }
      chart.series.each(function (chartSeries) {
        // if(chartSeries !== highlight.current){
        if ("strokes" in chartSeries) {
          if(chartSeries.className === "StepLineSeries"){
            // for digital line
            chartSeries.strokes.template.setAll({
              strokeOpacity: 1,
              strokeWidth: 3,
              stroke: chartSeries.get("fill"),
             });
          }else{
            //for analog line
            chartSeries.strokes.template.setAll({
              strokeOpacity: 1,
              strokeWidth: 1,
              stroke: chartSeries.get("fill"),
            });
          }
        } else if ("columns" in chartSeries) {
          // for bar chart
          chartSeries.columns.template.setAll({
            strokeOpacity: 1,
            strokeWidth: 1,
            fill: chartSeries.get("fill"),
            fillOpacity: 1,
          });
        }
        // }
      });
      const rect = itemContainer._settings.background;
      rect.set("stroke", am5.color(COLORS.baseColor));
    });
    v.events.on("click", (e) => {
      if(!e.target.dataItem || flg) return;
      while (e.target.children.length > 3) e.target.children.pop();
      if (e.target.dataItem.dataContext.isHidden()) {
        e.target.dataItem.dataContext.show();
        e.target.children.push(visibleIcon(root));
      } else {
        e.target.dataItem.dataContext.hide();
        v.children.push(notVisibleIcon(root));
      }
      v.children.push(hoveredLine(root, e.target.width(), e.target.height()));
    });
  });
};

export function handleScrollBarByKeyboard (event) {
  event.preventDefault();
  const step = 0.01;
  const startX = this.scrollbarX.get('start');
  const endX = this.scrollbarX.get('end');
  const diffX = endX - startX;
  const startY = this.scrollbarY.get('start');
  const endY = this.scrollbarY.get('end');
  const diffY = endY - startY;
  if (event.key === "ArrowLeft") {
    if(startX < step){
      this.scrollbarX.set("start", 0);
      this.scrollbarX.set("end", diffX);
    }else{
      this.scrollbarX.set("start", startX - step);
      this.scrollbarX.set("end", endX - step);
    }
  } else if (event.key === "ArrowRight") {
    if(endX > 1 - step){
      this.scrollbarX.set("start", 1 - diffX);
      this.scrollbarX.set("end", 1);
    }else{
      this.scrollbarX.set("start", startX + step);
      this.scrollbarX.set("end", endX + step);
    }
  } else if(event.key === "ArrowUp") {
    if(startY < step){
      this.scrollbarY.set("start", 0);
      this.scrollbarY.set("end", diffY);
    }else{
      this.scrollbarY.set("start", startY - step);
      this.scrollbarY.set("end", endY - step);
    }
  } else if(event.key === "ArrowDown") {
    if(endY > 1 - step){
      this.scrollbarY.set("start", 1 - diffY);
      this.scrollbarY.set("end", 1);
    }else{
      this.scrollbarY.set("start", startY + step);
      this.scrollbarY.set("end", endY + step);
    }
  }
}

export const visibleIcon = (root) => {
   return am5.Graphics.new(root, {
      fill: am5.color("#fff"),
      fillOpacity: 1,
      width: 20,
      height: 20,
      centerY: am5.p50,
      marginLeft: 5,
      layer: 50,
      svgPath: visibilityIcon,
    });
}

export const notVisibleIcon = (root) => {
  return am5.Graphics.new(root, {
      fill: am5.color("#fff"),
      fillOpacity: 1,
      width: 20,
      height: 20,
      centerY: am5.p50,
      marginLeft: 5,
      layer: 50,
      svgPath: hideIcon,
    });
}

export const blankIcon = (root) => {
    return am5.Graphics.new(root, {
      fillOpacity: 1,
      width: 20,
      height: 20,
      centerY: am5.p50,
      marginLeft: 5,
      layer: 50,
    });
}

export const hoveredLine = (root, width, height) => {
  return am5.Line.new(root, {
        stroke: am5.color('#fff'),
        strokeWidth: 3,
        y: height + 2,
        strokeOpacity: 0.5,
        isMeasured: false,
        position: 'absolute',
        width: width,
      })
}